import React, { useEffect, useRef, useState, createRef } from "react";
import classnames from "classnames";
import {
  Button,
  CircularProgress,
  stylize,
  TextField,
} from "@envestnet/envreact-component-library";
import withIntl from "utils/withIntl";
import messages from "./messages";
import styles, { Styles } from "./styles";
import EmailIconCircle from "images/EmailIconCircle";
import AuthenticatorHeader from "components/AuthenticatorHeader";
import Logo from "images/Logo";
import { mfaFlow } from "utils/enum";
import storage from "persistance";

const RESEND_COUNT_KEY = "emailOtpResendCount";
const MAX_RESEND_ATTEMPTS = 5;

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;

  mode: mfaFlow;
  onSend: (flow: mfaFlow) => void;
  onVerify: (code: string) => void;
  onRemove: () => void;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
  username?: string;
}

interface DefaultProps {}

export interface EmailOtpProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;
}

const defaultProps: DefaultProps = {};

type Props = InjectedProps & EmailOtpProps;

const EmailOtp = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    mode,
    onSend,
    onVerify,
    onCancel,
    loading,
    error,
    username,
  } = props;

  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [codeSent, setCodeSent] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [resendCount, setResendCount] = useState(
    Number(storage.getItem(RESEND_COUNT_KEY)) || 0
  );
  const maxResendReached = resendCount >= MAX_RESEND_ATTEMPTS;
  const digitRefs = useRef(
    digits.map(() => createRef<HTMLInputElement | HTMLTextAreaElement>())
  );

  const code = digits.join("");

  useEffect(() => {
    onSend(mode);
    setCodeSent(true);
  }, []);

  useEffect(() => {
    if (resendCountdown <= 0) {
      return undefined;
    }
    const timer = setInterval(() => {
      setResendCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  const handleResend = () => {
    if (maxResendReached) {
      return;
    }
    const nextCount = resendCount + 1;
    storage.setItem(RESEND_COUNT_KEY, String(nextCount));
    setResendCount(nextCount);
    setDigits(["", "", "", "", "", ""]);
    onSend(mode);
    setCodeSent(true);
    setResendCountdown(30);
  };

  const handleDigitChange = (index: number, value: string) => {
    // keep only the last digit typed for this box
    const digit = value.replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < digitRefs.current.length - 1) {
      digitRefs.current[index + 1]?.current?.focus();
    }
  };

  const getEmailOtpScreen = () => (
    <>
      <div
        className={classes.passcodeTextfieldContainer}
        style={{ marginBottom: error ? 8 : 16 }}
      >
        {digits.map((digit, index) => (
          <TextField
            key={index}
            inputRef={digitRefs.current[index]}
            onChangeText={(val) => handleDigitChange(index, val)}
            className={classes.passcodeCharacterTextfield}
            text={digit}
            maxLength={1}
            textFieldStyle={{
              borderRadius: 0,
              height: "45px",
            }}
            textStyle={{ textAlign: "center" }}
            data-testid={`email-otp-digit-${index}`}
          />
        ))}
      </div>
      {error && (
        <div
          className={classnames(
            classes.passcodeError,
            "header-text-font-family"
          )}
          data-testid="email-otp-error"
        >
          {error}
        </div>
      )}

      <div className={classes.otpInformation}>
        {formatMessage(messages.otpInformationMessage)}
      </div>

      {maxResendReached && (
        <div
          className={classnames(
            classes.exceededAttemptPasscodeError,
            "header-text-font-family"
          )}
          data-testid="email-otp-resend-max-error"
        >
          {formatMessage(messages.errorMaxResendAttempts)}
        </div>
      )}

      <div
        className={classnames(
          mode === mfaFlow.MFA
            ? classes.mfaButtonContainer
            : classes.activationButtonContainer
        )}
      >
        <div
          className={classnames(
            mode === mfaFlow.MFA
              ? classes.mfaActionButtonsContainer
              : classes.activationActionButtonsContainer
          )}
        >
          <Button
            buttonStyle="secondary"
            buttonSize="regular"
            name={formatMessage(messages.cancelButton)}
            onClick={onCancel}
            data-testid="email-otp-cancel"
          />
          <Button
            buttonStyle="primary"
            buttonSize="regular"
            name={formatMessage(messages.submitButton)}
            onClick={() => onVerify(code)}
            disabled={loading}
            data-testid="email-otp-submit"
          />
        </div>
        <Button
          buttonStyle={mode === mfaFlow.MFA ? "tertiary" : "secondary"}
          buttonSize="regular"
          name={
            resendCountdown > 0
              ? formatMessage(messages.resendCountdown, {
                  count: resendCountdown,
                })
              : formatMessage(messages.resendButton)
          }
          onClick={handleResend}
          disabled={loading || resendCountdown > 0 || maxResendReached}
          data-testid="email-otp-resend"
        />
      </div>
    </>
  );

  const getMfaEmailOtpScreen = () => (
    <div className={classes.mfaOtpScreenContainer}>
      <div>
        <EmailIconCircle />
      </div>
      <div>
        <div className={classes.rightContainer}>
          <div className={classes.mfaTitle}>
            {formatMessage(messages.mfaTitle)}
          </div>
          <div className={classes.mfaDescription}>
            {formatMessage(messages.mfaDescription)}
          </div>
          <div className={classes.mfaLabel}>
            {formatMessage(messages.mfaLabel)}
          </div>
        </div>
        <div className={classes.mfaOtpSection}>{getEmailOtpScreen()}</div>
      </div>
    </div>
  );

  const getActivationEmailOtpScreen = () => (
    <>
      <Logo />
      <AuthenticatorHeader
        userId={username || ""}
        authenticatorName="Email OTP"
      />
      <div className={classes.otpCodeDescription}>
        {formatMessage(messages.subtitle)}
      </div>
      <div className={classes.mfaOtpSection}>{getEmailOtpScreen()}</div>
    </>
  );

  if (mode === mfaFlow.MFA) {
    return getMfaEmailOtpScreen();
  }
  if (mode === mfaFlow.SECURITY) {
    return getActivationEmailOtpScreen();
  }

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div
        style={{ fontWeight: 600, marginBottom: 8 }}
        className="header-font-family"
      >
        {formatMessage(messages.title)}
      </div>

      {!codeSent ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CircularProgress size={20} />
          <span className="header-text-font-family">
            {formatMessage(messages.sending)}
          </span>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 16 }} className="header-text-font-family">
            {formatMessage(messages.subtitle)}
          </div>

          {loading && (
            <div style={{ marginTop: 12 }}>
              <CircularProgress size={20} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

EmailOtp.defaultProps = defaultProps;

export default withIntl(stylize<Props, DefaultProps>(styles)(EmailOtp));
